/**
 * /os - OS engagement owner surface shell (PRD-004 Batch 2, owner surface;
 * final design pass shell header).
 *
 * A SEPARATE top-level surface from /portal. The OS owner is a `subscribers`
 * row in `status='pending'` (Route A, scoping pass §1), which the portal entry
 * guard bounces by construction. This route's `beforeLoad` runs
 * `requireOsOwnerEntry` (lib/os-guard.ts), which serves a pending subscriber if
 * and only if they own a non-deleted `os_engagements` row (proven by RLS).
 *
 * The root route (__root.tsx) carries no global beforeLoad, so `/os` needs no
 * exclusion from any parent guard; this layout's guard is the only gate.
 *
 * The component renders the slim warm-brand shell from the Claude Design
 * prototype (design-final/Studio OS.html): the navy-on-linen logo, a hairline
 * divider, an "Operating System" overline, then the owner's name + initials
 * avatar on the right. It does NOT reuse PortalLayout: that layout is keyed on
 * `tier` / `subscriberStatus`, billing concepts an OS owner does not carry
 * (scoping pass §9: no billing semantics on a comped/included owner). All
 * `/os/*` children nest into this outlet.
 *
 * The owner's display name + initials are derived from the authenticated
 * session email (the OwnOsEngagement projection carries no name; we do not
 * re-fetch the subscriber row just for the header). Operators viewing the
 * surface for QA see their own session email, which is correct.
 */

import { useEffect, useState } from 'react';
import { Outlet, createFileRoute } from '@tanstack/react-router';
import { getCurrentSession } from '@/lib/auth';
import { requireOsOwnerEntry } from '@/lib/os-guard';

export const Route = createFileRoute('/os')({
  beforeLoad: async ({ location }) => {
    const os = await requireOsOwnerEntry({ redirectFrom: location.pathname });
    return { os };
  },
  component: OsShell,
});

/**
 * Derive a display name + initials from an email local-part. "maren.holt@x.io"
 * -> { name: "Maren Holt", initials: "MH" }. Falls back gracefully for an empty
 * or single-token local part.
 */
function nameFromEmail(email: string | null): { name: string; initials: string } {
  if (!email) return { name: 'Owner', initials: 'O' };
  const local = email.split('@')[0] ?? email;
  const tokens = local
    .split(/[._-]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
  if (tokens.length === 0) return { name: 'Owner', initials: 'O' };
  const name = tokens
    .map((t) => (t[0]?.toUpperCase() ?? '') + t.slice(1))
    .join(' ');
  const first = tokens[0]?.[0] ?? '';
  const last = tokens.length > 1 ? (tokens[tokens.length - 1]?.[0] ?? '') : '';
  const initials = `${first}${last}`.toUpperCase() || 'O';
  return { name, initials };
}

function OsShell() {
  const [owner, setOwner] = useState<{ name: string; initials: string } | null>(
    null,
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const session = await getCurrentSession();
        if (cancelled) return;
        setOwner(nameFromEmail(session?.user?.email ?? null));
      } catch {
        if (!cancelled) setOwner(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-[color:var(--color-white)] text-[color:var(--color-black)]">
      <header className="flex items-center justify-between border-b border-[color:var(--color-rule)] px-5 py-5 lg:px-14">
        <div className="flex items-center gap-3.5">
          <img
            src="/logo-navy-linen.svg"
            alt="Yes Please Studio"
            className="block h-7 w-auto"
          />
          <span
            className="h-[18px] w-px bg-[color:var(--color-line-dark)]"
            aria-hidden="true"
          />
          <span className="font-semibold text-[10.5px] uppercase tracking-[0.14em] text-[color:var(--color-text-mute)]">
            Operating System
          </span>
        </div>
        {owner ? (
          <div className="flex items-center gap-2.5">
            <span className="text-[13px] font-medium text-[color:var(--color-black)]">
              {owner.name}
            </span>
            <span className="flex h-7 w-7 items-center justify-center rounded-full border border-[color:var(--color-line-dark)] bg-[color:var(--color-linen)] text-[11px] font-semibold text-[color:var(--color-gray-500)]">
              {owner.initials}
            </span>
          </div>
        ) : null}
      </header>
      <main className="mx-auto w-full max-w-3xl flex-1 px-5 py-12 lg:px-10 lg:py-20">
        <Outlet />
      </main>
    </div>
  );
}
